import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  link?: string;
  isRead: boolean;
  createdAt: string;
}

const TYPE_CONFIG: Record<string, { icon: string; color: string }> = {
  PAYMENT_DUE: { icon: 'solar:calendar-bold-duotone', color: '#F59E0B' },
  PAYMENT_OVERDUE: { icon: 'solar:danger-triangle-bold-duotone', color: '#EF4444' },
  SALE_COMPLETED: { icon: 'solar:check-circle-bold-duotone', color: '#22C55E' },
  LOT_RESERVED: { icon: 'solar:flag-bold-duotone', color: '#3B82F6' },
  LOT_RELEASED: { icon: 'solar:undo-left-bold-duotone', color: '#8B5CF6' },
  COMMISSION_EARNED: { icon: 'solar:wallet-money-bold-duotone', color: '#0D9488' },
  SYSTEM: { icon: 'solar:bell-bold-duotone', color: '#6366F1' },
};

const timeAgo = (date: string) => {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const NotificationBell: React.FC = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;
    try {
      const data = await api.notifications.list(user.id);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch { /* silent */ }
  }, [user?.id]);

  // Fetch on mount and poll every 30s
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleMarkRead = async (id: string) => {
    try {
      await api.notifications.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };

  const handleMarkAllRead = async () => {
    if (!user?.id) return;
    try {
      await api.notifications.markAllRead(user.id);
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.notifications.delete(id);
      setNotifications(prev => {
        const n = prev.find(x => x.id === id);
        if (n && !n.isRead) setUnreadCount(c => Math.max(0, c - 1));
        return prev.filter(x => x.id !== id);
      });
    } catch { /* silent */ }
  };

  const handleGenerate = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      await api.notifications.generate(user.id);
      await fetchNotifications();
    } catch { /* silent */ }
    setLoading(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        style={{
          position: 'relative', background: 'none', border: 'none',
          cursor: 'pointer', padding: '6px', borderRadius: 'var(--radius-md)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(148,163,184,0.1)')}
        onMouseLeave={e => (e.currentTarget.style.background = 'none')}
      >
        <Icon icon="solar:bell-bold-duotone" width={22} style={{ color: 'var(--text-secondary)' }} />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: '2px', right: '2px',
            width: unreadCount > 9 ? '18px' : '16px', height: '16px',
            borderRadius: '8px', background: '#EF4444',
            color: '#fff', fontSize: '9px', fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid var(--bg-surface)',
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: '8px',
          width: '380px', maxHeight: '480px',
          background: 'var(--modal-bg)', backdropFilter: 'blur(20px)',
          border: '1px solid var(--border-base)', borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--modal-shadow)',
          overflow: 'hidden', zIndex: 1000,
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Header */}
          <div style={{
            padding: '14px 16px', borderBottom: '1px solid var(--border-base)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>Notifications</span>
              {unreadCount > 0 && (
                <span style={{
                  padding: '2px 8px', borderRadius: '10px', background: 'rgba(239,68,68,0.15)',
                  color: '#EF4444', fontSize: '11px', fontWeight: 700,
                }}>
                  {unreadCount} new
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button onClick={handleGenerate} title="Check for new alerts"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(148,163,184,0.1)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                <Icon icon={loading ? 'solar:refresh-bold-duotone' : 'solar:refresh-circle-bold-duotone'}
                  width={16} style={{ color: 'var(--text-muted)' }} />
              </button>
              {unreadCount > 0 && (
                <button onClick={handleMarkAllRead} title="Mark all as read"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(148,163,184,0.1)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                  <Icon icon="solar:check-read-bold-duotone" width={16} style={{ color: 'var(--text-muted)' }} />
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto', maxHeight: '400px' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <Icon icon="solar:bell-off-bold-duotone" width={36} style={{ color: 'var(--text-faint)', marginBottom: '8px' }} />
                <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No notifications yet</div>
                <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '4px' }}>
                  Click refresh to check for alerts
                </div>
              </div>
            ) : (
              notifications.map(n => {
                const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.SYSTEM;
                return (
                  <div
                    key={n.id}
                    onClick={() => { if (!n.isRead) handleMarkRead(n.id); }}
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid var(--border-base)',
                      background: n.isRead ? 'transparent' : 'rgba(13,148,136,0.04)',
                      cursor: 'pointer', transition: 'background 0.15s',
                      display: 'flex', gap: '10px',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(148,163,184,0.06)')}
                    onMouseLeave={e => (e.currentTarget.style.background = n.isRead ? 'transparent' : 'rgba(13,148,136,0.04)')}
                  >
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '8px', flexShrink: 0,
                      background: `${cfg.color}15`, display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon icon={cfg.icon} width={16} style={{ color: cfg.color }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <span style={{
                          color: n.isRead ? 'var(--text-secondary)' : 'var(--text-primary)',
                          fontSize: '12px', fontWeight: n.isRead ? 500 : 700,
                        }}>
                          {n.title}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                          <span style={{ color: 'var(--text-faint)', fontSize: '10px' }}>{timeAgo(n.createdAt)}</span>
                          {!n.isRead && (
                            <div style={{ width: '6px', height: '6px', borderRadius: '3px', background: '#0D9488' }} />
                          )}
                        </div>
                      </div>
                      <div style={{
                        color: 'var(--text-muted)', fontSize: '11px', marginTop: '2px',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {n.message}
                      </div>
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); handleDelete(n.id); }}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        padding: '2px', opacity: 0.4, flexShrink: 0, alignSelf: 'center',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                      onMouseLeave={e => (e.currentTarget.style.opacity = '0.4')}
                    >
                      <Icon icon="solar:close-circle-bold-duotone" width={14} style={{ color: 'var(--text-muted)' }} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
